<template>
  <div class="bg-white dark:bg-gray-900 flex grow place-content-center overflow-hidden">
    <div
      v-if="breakpoints.mdAndUp || showList"
      class="w-full md:w-2/5 lg:w-1/3 overflow-y-auto"
    >
      <ol v-if="schools && schools.length" class="flex flex-col divide-y divide-gray-100">
        <school-card
          v-for="school in schools"
          :key="school.id"
          class="p-4 ring-inset"
          tag="li"
          :school="school"
          @update:school="updateSchool"
          @mouseenter="setHoveredMarker(school.id)"
          @mouseleave="setHoveredMarker(null)"
        />
      </ol>

      <div v-else class="p-5">
        <icon set="duotone" name="clipboardListCheck" class="text-7xl text-gray-300 w-auto mb-5"/>
        <p class="font-medium text-2xl text-blue dark:text-white mb-3">
          Add schools to your list
        </p>
        <p class="text-gray-400 mb-8">
          You haven't added any schools yet.
          When you find a school you like, click the <icon name="star" class="text-yellow"/> to save it for later.
        </p>

        <div>
          <a :href="route('schools.index')" class="btn btn-lg btn-primary">
            Start exploring
          </a>
        </div>
      </div>
    </div>

    <focus-trap
      :active="breakpoints.smAndDown && showMap"
      :class="{ hidden: breakpoints.smAndDown && !showMap }"
    >
      <div
        class="flex-1 size-full md:w-auto md:block md:relative"
        :class="{ 'fixed top-0 left-0': breakpoints.smAndDown }"
        tabindex="-1"
      >
        <l-button
          v-if="breakpoints.smAndDown"
          class="bg-white dark:bg-gray-900 text-lg leading-none text-blue dark:text-white shadow p-2 fixed z-10 top-3 left-3 rounded-sm"
          title="Close map"
          @click="toggleView"
        >
          <icon name="close"/>
        </l-button>

        <GMapMap
          class="h-full"
          :center="center || { lng: -89, lat: 44 }"
          :zoom="5"
          :options="{
            minZoom: 3,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            gestureHandling: 'greedy',
          }"
          @click="openedMarker = null"
        >
          <GMapMarker
            v-for="school of schools"
            :key="school.id"
            :position="{ lng: school.location.coordinates[0], lat: school.location.coordinates[1] }"
            :icon="getMarkerIcon(school)"
            :options="getMarkerOptions(school)"
            @click="openMarker(school.id)"
          >
            <GMapInfoWindow :opened="openedMarker === school.id">
              <div class="p-2 md:p-3" tabindex="0">
                <school-card
                  :school="school"
                  @update:school="updateSchool"
                />
              </div>
            </GMapInfoWindow>
          </GMapMarker>
        </GMapMap>
      </div>
    </focus-trap>

    <l-button
      v-if="breakpoints.smAndDown && schools?.length"
      class="
        fixed bottom-4 z-10
        bg-gray-700 text-gray-100 font-semibold
        py-2 px-4 rounded-full shadow-lg
        flex items-center gap-2
      "
      @click="toggleView"
    >
      <template v-if="showList">
        Map
        <icon name="map"/>
      </template>
      <template v-else>
        List
        <icon name="bars"/>
      </template>
    </l-button>
  </div>
</template>

<script setup>
import Icon from "@/components/Icon.vue"
import LButton from "@/components/buttons/LButton.vue"
import SchoolCard from "@/components/cards/SchoolCard.vue"
import route from "ziggy-js"
import { FocusTrap } from "focus-trap-vue"
import { breakpoints } from "@/breakpoints"
import { computed, ref } from "vue"

const schools = defineModel("schools", { type: Array })

defineProps({
  center: { type: Object },
})

const openedMarker = ref(null)
const hoveredMarker = ref(null)
const showList = ref(true)
const showMap = computed(() => !showList.value)

const toggleView = () => {
  showList.value = !showList.value
}

const jobScoreColors = [
  "#ffd0d4",
  "#ffb8ac",
  "#ffb689",
  "#ffc468",
  "#ffdf48",
  "#f8ff29",
  "#b3df21",
  "#76bf19",
  "#439f13",
  "#1b800d",
]

const openMarker = (schoolId) => {
  openedMarker.value = schoolId
}

const setHoveredMarker = (schoolId) => {
  hoveredMarker.value = schoolId
}

const getMarkerIcon = (school) => {
  const size = 40

  const color = (() => {
    if (isMarkerActive(school)) {
      return "#000"
    }

    return jobScoreColors[Math.floor(school.job_score)] || "#fff"
  })()

  if (!school.job_score) {
    return `data:image/svg+xml;base64,${btoa(`
      <svg width="${size}" height="${size}" viewBox="0 0 312 312" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g filter="url(#filter0_d_1_6)">
          <circle cx="154" cy="150" r="150" fill="${color}"/>
          <path
            d="M124.429 120.745L126.769 120.404L127.815 118.283L148.808 75.7189C150.927 71.4269 157.06 71.4264 159.193 75.7216C159.194 75.7229 159.195 75.7242 159.195 75.7255L180.185 118.283L181.231 120.404L183.571 120.745L230.542 127.593L230.545 127.594C235.272 128.279 237.179 134.123 233.742 137.47L233.741 137.471L199.759 170.585L198.064 172.237L198.464 174.57L206.501 221.344C207.309 226.064 202.326 229.652 198.114 227.437L198.113 227.436L156.094 205.35L154 204.249L151.906 205.35L109.887 227.436L109.88 227.44L109.873 227.444C105.693 229.66 100.689 226.093 101.498 221.347C101.498 221.347 101.498 221.347 101.498 221.347C101.499 221.346 101.499 221.345 101.499 221.344L109.536 174.57L109.937 172.237L108.241 170.585L74.2592 137.471L74.2579 137.47C70.8208 134.123 72.7277 128.279 77.455 127.594L77.4583 127.593L124.429 120.745Z"
            fill="${school.in_list ? "#FDC107" : "none"}"
            stroke="#FDC107"
            stroke-width="11"
          />
        </g>
        <defs>
          <filter id="filter0_d_1_6" x="0" y="0" width="312" height="312" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
            <feFlood flood-opacity="0" result="BackgroundImageFix"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="2" dy="6"/>
            <feGaussianBlur stdDeviation="3"/>
            <feComposite in2="hardAlpha" operator="out"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0"/>
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1_6"/>
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1_6" result="shape"/>
          </filter>
        </defs>
      </svg>
  `)}`
  }

  return `data:image/svg+xml;base64,${btoa(`
    <svg width="${size}" height="${size}" viewBox="0 0 228 228" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g filter="url(#filter1)">
        <circle cx="115" cy="110" r="100" fill="${color}"/>
      </g>
      <defs>
        <filter id="filter1" x="0" y="0" width="228" height="228" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
          <feFlood flood-opacity="0" result="BackgroundImageFix"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feMorphology radius="3" operator="dilate" in="SourceAlpha" result="effect1_dropShadow_1_3"/>
          <feOffset dx="-1" dy="4"/>
          <feGaussianBlur stdDeviation="5.5"/>
          <feComposite in2="hardAlpha" operator="out"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"/>
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1_3"/>
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1_3" result="shape"/>
        </filter>
      </defs>
    </svg>
  `)}`
}

const getMarkerOptions = (school) => {
  return {

    label: school.job_score ? {
      className: "leading-none",
      color: school.job_score >= 8 || isMarkerActive(school) ? "#fff" : "#000",
      text: `${school.job_score}` || "–",
      fontFamily: "Source Sans 3, arial, helvetica, sans-serif",
      fontSize: "16px",
      fontWeight: "600",
    } : {
      text: "hello",
      color: "#fff",
    },
  }
}

const isMarkerActive = (school) => {
  return hoveredMarker.value === school.id || openedMarker.value === school.id
}

const updateSchool = (newSchool) => {
  const index = schools.value.findIndex(s => s.id === newSchool.id)

  if (schools.value[index]) {
    schools.value[index] = newSchool
  }
}
</script>

<style>
  /* hide close button on info window */
  .gm-ui-hover-effect {
    display: none !important;
  }

  /* set font-family of info window */
  .gm-style {
    font-family: "Source Sans 3", arial, helvetica, sans-serif;
  }

  /* remove infowindow padding */
  .gm-style .gm-style-iw-c {
    padding: 0 !important;
  }

  .gm-style-iw-d {
    overflow: hidden !important;
  }

  .vue-map button:focus, .vue-map a:focus {
    border: none !important;
    outline: 0 !important;
  }
</style>
